import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, School, Sparkles } from "lucide-react";
import { SITE_LABEL } from "@/lib/sites";

// Copy for the "welcome banner + role tile + what you can do" sections, keyed by role.
// Falls back to "user" when the role isn't recognized.
const ROLE_COPY = {
  user: {
    title:      "You're set up as a basic user for now",
    detail:     "You can submit and track your own tickets right away. A district administrator will review your account and grant additional access (Teacher, Secretary, Principal, or a department role) if it fits your job.",
    roleLabel:  "User",
    roleBlurb:  "Submit + track own tickets",
    banner:     "amber",
    canDo:      [
      "Submit a new ticket for IT, Maintenance, Custodial, or Mechanic issues",
      "Track status updates on your own tickets",
      "Mark your ticket Resolved or Closed once the issue is fixed",
    ],
  },
  teacher: {
    title:     "Welcome, Teacher",
    detail:    "Your Entra group is mapped to the Teacher role. You have full ticket-tracking access at your assigned site.",
    roleLabel: "Teacher",
    roleBlurb: "Submit + follow tickets at your site",
    banner:    "blue",
    canDo:     [
      "Submit tickets for IT, Maintenance, Custodial, or Mechanic issues",
      "Track your requests as staff pick them up",
      "Add follow-up comments so the tech has context",
    ],
  },
  secretary: {
    title:     "Welcome, Site Secretary",
    detail:    "You can triage tickets across every department at your assigned site.",
    roleLabel: "Secretary",
    roleBlurb: "Site-wide read across all departments",
    banner:    "blue",
    canDo:     [
      "See every open ticket at your site regardless of department",
      "Nudge assignees or reassign as coverage changes",
      "Track resolution progress for the front office",
    ],
  },
  principal: {
    title:     "Welcome, Principal",
    detail:    "You have oversight of every ticket at your assigned site.",
    roleLabel: "Principal",
    roleBlurb: "Site-wide oversight",
    banner:    "blue",
    canDo:     [
      "Monitor open + closed tickets across every department at your site",
      "Escalate critical issues directly to district IT / maintenance leads",
      "Review ticket history for annual reporting",
    ],
  },
  it: {
    title:     "Welcome, IT Team",
    detail:    "Your Entra group is mapped to the IT role. You can pick up any IT ticket at your assigned sites.",
    roleLabel: "IT Staff",
    roleBlurb: "IT department · assigned sites",
    banner:    "emerald",
    canDo:     [
      "Claim + assign IT tickets across your sites",
      "Update ticket status as work progresses",
      "Attach receipts, screenshots, or repair notes for the audit trail",
    ],
  },
  maintenance: {
    title:     "Welcome, Maintenance Team",
    detail:    "Your Entra group is mapped to the Maintenance role. You can work Maintenance tickets and view Custodial tickets at your sites.",
    roleLabel: "Maintenance",
    roleBlurb: "Maintenance + read-only Custodial",
    banner:    "emerald",
    canDo:     [
      "Claim + assign Maintenance tickets at your sites",
      "View Custodial tickets to coordinate with the custodial team",
      "Log parts, hours, and progress notes on every ticket",
    ],
  },
  custodian: {
    title:     "Welcome, Custodial Team",
    detail:    "Your Entra group is mapped to the Custodial role. You can pick up Custodial tickets at your assigned sites.",
    roleLabel: "Custodial",
    roleBlurb: "Custodial department · assigned sites",
    banner:    "emerald",
    canDo:     [
      "Claim + assign Custodial tickets at your sites",
      "Update ticket status as work progresses",
      "Add before/after photos and notes for the audit trail",
    ],
  },
  mechanic: {
    title:     "Welcome, Mechanic Team",
    detail:    "Your Entra group is mapped to the Mechanic role. You can pick up transportation and fleet tickets at your assigned sites.",
    roleLabel: "Mechanic",
    roleBlurb: "Fleet / transportation tickets",
    banner:    "emerald",
    canDo:     [
      "Claim + assign Mechanic tickets across your sites",
      "Update ticket status as work progresses",
      "Log parts, hours, and vehicle notes for the audit trail",
    ],
  },
  admin: {
    title:     "Welcome, Administrator",
    detail:    "You have full district-wide access. Head to Settings when you're ready to configure branding, SSO, SMTP, and backups.",
    roleLabel: "Administrator",
    roleBlurb: "District-wide access",
    banner:    "emerald",
    canDo:     [
      "See every ticket across all sites + all departments",
      "Manage users, roles, and site scopes",
      "Configure SSO group sync, SMTP, backups, and branding",
    ],
  },
};

const BANNER_TONES = {
  amber:   { border: "border-amber-200",   bg: "bg-amber-50",   icon: "text-amber-700",   title: "text-amber-900", detail: "text-amber-900/80" },
  blue:    { border: "border-blue-200",    bg: "bg-blue-50",    icon: "text-blue-700",    title: "text-blue-900",  detail: "text-blue-900/80"  },
  emerald: { border: "border-emerald-200", bg: "bg-emerald-50", icon: "text-emerald-700", title: "text-emerald-900", detail: "text-emerald-900/80" },
};

export default function SsoWelcome() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);

  // If a non-SSO user (or already acknowledged) lands here directly, send them home
  useEffect(() => {
    if (!user) return;
    if (!user.sso_provisioned || user.welcome_seen) nav("/", { replace: true });
  }, [user, nav]);

  const acknowledge = async () => {
    setBusy(true);
    try {
      await api.post("/auth/welcome/ack");
      await refresh();
      nav("/tickets", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const primarySite = user?.sites?.[0];
  const copy = ROLE_COPY[user?.role] || ROLE_COPY.user;
  const tone = BANNER_TONES[copy.banner] || BANNER_TONES.amber;

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6" data-testid="sso-welcome-page">
      <div className="w-full max-w-xl bg-white border border-zinc-200 rounded-md shadow-sm overflow-hidden">
        {/* Dark header */}
        <div className="bg-zinc-950 text-zinc-50 px-8 py-6 flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-white text-zinc-950 grid place-items-center">
            <Sparkles className="h-5 w-5" strokeWidth={2.2} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">ESUSD HelpDesk</div>
            <div className="font-heading text-xl font-bold tracking-tight" data-testid="sso-welcome-greeting">Welcome, {user?.name?.split(" ")[0] || "there"}!</div>
          </div>
        </div>

        <div className="px-8 py-7 space-y-5">
          <p className="text-sm text-zinc-700 leading-relaxed">
            Your Microsoft account has been linked to the district HelpDesk. You're signed in as
            <span className="font-medium text-zinc-950"> {user?.email}</span>.
          </p>

          <div className={`rounded-md border ${tone.border} ${tone.bg} p-4`} data-testid="sso-welcome-banner">
            <div className="flex items-start gap-2">
              <ShieldCheck className={`h-4 w-4 ${tone.icon} mt-0.5 shrink-0`} />
              <div className={`text-sm ${tone.title}`}>
                <div className="font-medium" data-testid="sso-welcome-role-title">{copy.title}</div>
                <div className={`text-xs ${tone.detail} mt-1 leading-relaxed`}>
                  {copy.detail}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-md border border-zinc-200 p-3">
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Your role</div>
              <div className="mt-1 text-sm text-zinc-950 font-medium" data-testid="sso-welcome-role-label">{copy.roleLabel}</div>
              <div className="text-xs text-zinc-500">{copy.roleBlurb}</div>
            </div>
            <div className="rounded-md border border-zinc-200 p-3">
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 flex items-center gap-1">
                <School className="h-3 w-3" /> Assigned site
              </div>
              <div className="mt-1 text-sm text-zinc-950 font-medium truncate" title={SITE_LABEL[primarySite] || primarySite}>
                {SITE_LABEL[primarySite] || primarySite || "District"}
              </div>
              <div className="text-xs text-zinc-500">Editable by admin</div>
            </div>
          </div>

          <div className="text-xs text-zinc-500 leading-relaxed">
            <div className="font-medium text-zinc-700 mb-1">What you can do now</div>
            <ul className="space-y-1 list-disc pl-4">
              {copy.canDo.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>

          <Button
            onClick={acknowledge}
            disabled={busy}
            data-testid="sso-welcome-acknowledge"
            className="w-full h-11 rounded-md bg-[#0055FF] hover:bg-[#0040CC] text-white transition-colors duration-200"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            {busy ? "One moment…" : "Got it — take me to my tickets"}
          </Button>
        </div>
      </div>
    </div>
  );
}
